import { oc } from '@orpc/contract'
import { z } from 'zod'
import { MCP_NAME_MAX, MCP_URL_MAX, mcpServerSchema } from '../mcp'

/**
 * Project plugins — remote MCP servers per project (board wwfu).
 *
 * `list` is open to every active member (read-only pane); every mutation is
 * owner/admin only (FORBIDDEN otherwise). All procedures need the
 * organization's `mcpPlugins` flag (FORBIDDEN `FEATURE_DISABLED`); creating
 * also needs `MCP_TOKEN_KEY` (PRECONDITION_FAILED `NOT_CONFIGURED`).
 *
 * Error `data.reason`s: `FEATURE_DISABLED`, `NOT_CONFIGURED`, `INVALID_URL`
 * (`data.urlReason`), `LIMIT_REACHED`, `PROBE_FAILED` (`data.probeReason`,
 * `data.message`), `NOT_OAUTH`.
 */

const projectInput = z.object({ projectId: z.string().min(1) })
const serverInput = projectInput.extend({ serverId: z.string().min(1) })

export const mcpListContract = oc.input(projectInput).output(
  z.object({
    servers: z.array(mcpServerSchema),
    /** `MCP_TOKEN_KEY` is set on this Worker. */
    configured: z.boolean(),
    /** The caller is an owner/admin of the project. */
    canManage: z.boolean(),
  })
)

export const mcpCreateContract = oc
  .input(
    projectInput.extend({
      name: z.string().trim().min(1).max(MCP_NAME_MAX),
      url: z.string().trim().min(1).max(MCP_URL_MAX),
      /** Optional API key, sent as `Authorization: Bearer …`. */
      apiKey: z.string().trim().max(4096).optional(),
    })
  )
  .output(
    z.object({
      server: mcpServerSchema,
      /** Set for OAuth servers: send the browser here to authorise. */
      authorizeUrl: z.string().optional(),
    })
  )

export const mcpUpdateContract = oc
  .input(
    serverInput.extend({
      name: z.string().trim().min(1).max(MCP_NAME_MAX).optional(),
      enabled: z.boolean().optional(),
    })
  )
  .output(z.object({ server: mcpServerSchema }))

export const mcpRemoveContract = oc
  .input(serverInput)
  .output(z.object({ success: z.literal(true) }))

export const mcpStartAuthorizationContract = oc
  .input(serverInput)
  .output(z.object({ authorizeUrl: z.string() }))

export const mcpRefreshToolsContract = oc
  .input(serverInput)
  .output(z.object({ server: mcpServerSchema }))
